import { Component, computed, inject, input, model, output, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { User } from '../../../shared/models/user.model';

@Component({
    selector: 'app-user-edit-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        DropdownModule,
        CheckboxModule
    ],
    templateUrl: './user-edit-dialog.component.html',
    styleUrls: ['./user-edit-dialog.component.scss']
})
export class UserEditDialogComponent {
    private messageService = inject(MessageService);

    // Inputs
    user = input<User | null>(null);
    isAdminMode = input<boolean>(true);
    loading = input<boolean>(false);

    // Model for two-way visibility
    visible = model<boolean>(false);

    // Outputs
    save = output<Partial<User>>();
    cancel = output<void>();

    // Form signals
    login = signal('');
    name = signal('');
    email = signal('');
    password = signal('');
    role = signal<number>(0);
    isActive = signal(true);
    isStaff = signal(true);
    isSuperuser = signal(false);

    // Status
    isEditMode = computed(() => !!this.user());
    formSubmitted = signal(false);

    roles = [
        { label: 'Admin', value: 1 },
        { label: 'User', value: 0 }
    ];

    // Validation
    loginError = computed(() => {
        if (!this.formSubmitted()) return '';
        if (!this.login().trim()) return 'Login is required';
        return '';
    });

    nameError = computed(() => {
        if (!this.formSubmitted()) return '';
        if (!this.name().trim()) return 'Name is required';
        return '';
    });

    passwordError = computed(() => {
        if (!this.formSubmitted()) return '';
        const pw = this.password().trim();
        if (!this.isEditMode() && !pw) return 'Password is required';
        if (pw && pw.length < 6) return 'Password must be at least 6 characters';
        return '';
    });

    isFormValid = computed(() => {
        return !this.loginError() && !this.nameError() && !this.passwordError();
    });

    constructor() {
        // Sync form with user input
        effect(() => {
            const user = this.user();
            if (user) {
                this.login.set(user.login || '');
                this.name.set(user.name || '');
                this.email.set(user.email || '');
                this.password.set('');
                this.role.set(typeof user.role === 'string' ? parseInt(user.role) : user.role);
                this.isActive.set(user.is_active ?? true);
                this.isStaff.set(user.is_staff ?? true);
                this.isSuperuser.set(user.is_superuser ?? false);
            } else {
                this.resetForm();
            }
            this.formSubmitted.set(false);
        });
    }

    onSave(): void {
        this.formSubmitted.set(true);

        if (!this.isFormValid()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fix the errors in the form'
            });
            return;
        }

        const userData: Partial<User> = {
            login: this.login().trim(),
            name: this.name().trim(),
            email: this.email().trim(),
            role: this.role(),
            is_active: this.isActive(),
            is_staff: this.isStaff(),
            is_superuser: this.isSuperuser()
        };

        if (this.password().trim()) {
            userData.password = this.password().trim();
        }

        this.save.emit(userData);
    }

    onCancel(): void {
        this.visible.set(false);
        this.cancel.emit();
        this.resetForm();
    }

    private resetForm(): void {
        this.login.set('');
        this.name.set('');
        this.email.set('');
        this.password.set('');
        this.role.set(0);
        this.isActive.set(true);
        this.isStaff.set(true);
        this.isSuperuser.set(false);
        this.formSubmitted.set(false);
    }
}
